import { Calendar } from "lucide-react";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { DayPicker, type DateRange } from "react-day-picker";
import { useController, useFormContext } from "react-hook-form";

import {
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/primitives/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/primitives/popover";
import useTranslations from "@/shared/lang/useTranslations";

import {
  DATE_TRIGGER_CLASS,
  DAY_PICKER_CLASSNAMES,
  DAY_PICKER_STYLE,
  dateToIso,
  isoToDate,
  resolveDayPickerLocale,
} from "./colocaDayPicker.shared";
import { DayPickerChevron } from "./DayPickerChevron";
import { DayPickerDropdown } from "./DayPickerDropdown";

export type ColocaDateRangeFieldProps = {
  /** Form field holding the range start (`YYYY-MM-DD` string, `""` when unset). */
  nameFrom: string;
  /** Form field holding the range end (`YYYY-MM-DD` string, `""` when unset). */
  nameTo: string;
  /** Visible label rendered above the trigger. */
  label?: ReactNode;
  /** Helper text rendered below the trigger. */
  description?: ReactNode;
  /** Placeholder when no range is selected. Falls back to a localized "Pick a date". */
  placeholder?: string;
  /** Latest selectable date (inclusive) — e.g. `new Date()` for history filters. */
  maxDate?: Date;
  /** Earliest selectable date (inclusive). */
  minDate?: Date;
  /** Earliest year shown in the year dropdown. Defaults to 1900. */
  fromYear?: number;
  /** Latest year shown in the year dropdown. Defaults to current year. */
  toYear?: number;
  disabled?: boolean;
};

/**
 * Date-range picker field wired to the nearest React Hook Form context — one trigger, one calendar, two form
 * fields (`nameFrom` / `nameTo`).
 *
 * Designed for list filters (statements, history, audit): the calendar's range mode makes an inverted range
 * (`from > to`) impossible by construction, a half-open range (only `from`) is valid, and both values stay
 * ISO `YYYY-MM-DD` strings so existing `{ from, to }` filter schemas work unchanged.
 *
 * Dates are parsed and serialized as LOCAL days (never `new Date("YYYY-MM-DD")`, which shifts a day in
 * negative-offset timezones). Field updates are no-op-guarded so `form.watch` subscribers refetch once per
 * actual change, not once per field write.
 */
export function ColocaDateRangeField({
  nameFrom,
  nameTo,
  label,
  description,
  placeholder,
  maxDate,
  minDate,
  fromYear = 1900,
  toYear,
  disabled,
}: ColocaDateRangeFieldProps) {
  const { control } = useFormContext();
  const { field: toField } = useController({ name: nameTo, control });
  const { t, activeLanguage } = useTranslations("global");
  const [open, setOpen] = useState(false);
  const dpLocale = useMemo(() => resolveDayPickerLocale(activeLanguage), [activeLanguage]);

  const resolvedPlaceholder = placeholder ?? t("input_date_placeholder");

  const startMonth = useMemo(() => new Date(fromYear, 0, 1), [fromYear]);
  const endMonth = useMemo(() => {
    const year = toYear ?? new Date().getFullYear();
    return new Date(year, 11, 31);
  }, [toYear]);

  /**
   * The month the calendar opens on when nothing is picked yet: today's.
   *
   * It used to fall back to `endMonth`, which is December 31 of the last navigable year — so a filter opened
   * in September showed December, and picking "today" meant navigating back three months. `endMonth` is the
   * navigation _bound_, not a sensible starting view. When `maxDate` is in the past (a range that closed),
   * that month is the one worth showing instead.
   */
  const todayMonth = useMemo(() => {
    const today = new Date();
    return maxDate && maxDate < today ? maxDate : today;
  }, [maxDate]);

  const formatDisplay = useCallback(
    (date: Date) =>
      new Intl.DateTimeFormat(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(date),
    [],
  );

  return (
    <FormField
      control={control}
      name={nameFrom}
      render={({ field: fromField }) => {
        const fromDate = isoToDate(fromField.value as string | undefined);
        const toDate = isoToDate(toField.value as string | undefined);
        // DayPicker's DateRange is anchored on `from`; an orphan `to` (cleared start) renders as empty.
        const selected: DateRange | undefined = fromDate ? { from: fromDate, to: toDate } : undefined;
        const displayValue = fromDate
          ? `${formatDisplay(fromDate)} – ${toDate ? formatDisplay(toDate) : "…"}`
          : "";

        const handleSelect = (range: DateRange | undefined) => {
          const nextFrom = range?.from ? dateToIso(range.from) : "";
          const nextTo = range?.to ? dateToIso(range.to) : "";
          // Only write fields that actually changed — one watch emission per user action.
          if (nextFrom !== ((fromField.value as string | undefined) ?? "")) fromField.onChange(nextFrom);
          if (nextTo !== ((toField.value as string | undefined) ?? "")) toField.onChange(nextTo);
          if (range?.from && range?.to) setOpen(false);
        };

        return (
          <FormItem>
            {label && <FormLabel>{label}</FormLabel>}
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger
                render={
                  <button
                    type="button"
                    disabled={disabled}
                    aria-haspopup="dialog"
                    onBlur={fromField.onBlur}
                    className={DATE_TRIGGER_CLASS}
                  >
                    <span className={displayValue ? "" : "text-muted-foreground"}>
                      {displayValue || resolvedPlaceholder}
                    </span>
                    <Calendar size={16} className="shrink-0 text-muted-foreground" />
                  </button>
                }
              />
              <PopoverContent align="start" className="w-auto p-4">
                <DayPicker
                  mode="range"
                  selected={selected}
                  onSelect={handleSelect}
                  captionLayout="dropdown"
                  navLayout="after"
                  reverseYears
                  startMonth={startMonth}
                  endMonth={endMonth}
                  defaultMonth={toDate ?? fromDate ?? todayMonth}
                  disabled={[
                    ...(minDate ? [{ before: minDate }] : []),
                    ...(maxDate ? [{ after: maxDate }] : []),
                  ]}
                  showOutsideDays
                  locale={dpLocale}
                  style={DAY_PICKER_STYLE}
                  components={{ Chevron: DayPickerChevron, Dropdown: DayPickerDropdown }}
                  classNames={{
                    ...DAY_PICKER_CLASSNAMES,
                    // Range mode: solid endpoints, soft wash in between (the shared `selected` class would
                    // paint every day in the range solid).
                    selected: "",
                    range_start:
                      "[&_button]:bg-primary [&_button]:text-primary-foreground [&_button:hover]:bg-primary",
                    range_middle: "bg-primary/10 [&_button]:rounded-none",
                    range_end:
                      "[&_button]:bg-primary [&_button]:text-primary-foreground [&_button:hover]:bg-primary",
                  }}
                />
              </PopoverContent>
            </Popover>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
